import { Component, OnInit, AfterViewInit, Input, SimpleChanges, OnChanges, OnDestroy } from '@angular/core';
import { AccessService } from '@app-core/services/access/access.service';
import { initSuprSend, clearSuprSendInbox, ThemeType } from '@suprsend/web-components';
import { Router } from '@angular/router';
import { DataService } from '@app-core/services/data/data.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DvrService } from '@app-dvr/services/dvr/dvr.service';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { TranslateService } from '@ngx-translate/core';
import { IncidentsService } from '@app-incidents/services/incidents.service';

import {
  AllowedPaths,
  DiagnosticsQueryParams,
  IncidentDetailsParams,
  SafetyEventsPageQueryParams,
  VideoRequestsPageQueryParams,
} from './suprsend.model';
import { SUPRSEND_PUBLIC_API_KEY, SUPRSEND_WORKSPACE_KEY } from '@app-core/constants/constants';
import { SuprsendService } from './suprsend.service';

@Component({
  selector: 'app-suprsend-notification',
  templateUrl: './suprsend-notification.component.html',
  styleUrls: ['./suprsend-notification.component.scss'],
})
export class SuprsendNotificationComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  public userInfo: any;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  public distinctid: string;
  public subscriberid: string;
  @Input() user_distinct_id: string;
  @Input() user_subscriberId: string;
  public currentTheme: ThemeType = ThemeType.LIGHT;

  constructor(
    //private elementRef: ElementRef,
    private accessService: AccessService,
    private router: Router,
    public translate: TranslateService,
    public dataService: DataService,
    private dvrService: DvrService,
    private incidentsService: IncidentsService,
    private snackbarService: SnackBarService,
    private suprsendservice: SuprsendService
  ) {
    this.userInfo = this.accessService.getLoginInfo();
  }

  ngOnInit(): void {
    this.distinctid = this.user_distinct_id;
    this.subscriberid = this.user_subscriberId;

    this.dataService._currentTheme.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value) => {
      if (value) {
        this.currentTheme = value;
        this.initializeInbox();
      }
    });
  }

  ngAfterViewInit(): void {
    this.initializeInbox();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    clearSuprSendInbox();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const distinctIdChanged = changes['user_distinct_id'];
    const subscriberIdChanged = changes['user_subscriberId'];

    if ((distinctIdChanged && !distinctIdChanged.firstChange) || (subscriberIdChanged && !subscriberIdChanged.firstChange)) {
      this.distinctid = this.user_distinct_id;
      this.subscriberid = this.user_subscriberId;

      this.initializeInbox();
    }
  }

  private initializeInbox(): void {
    const suprSendConfig = {
      publicApiKey: SUPRSEND_PUBLIC_API_KEY,
      distinctId: this.distinctid,
      subscriberId: this.subscriberid,
      inbox: {
        workspaceKey: SUPRSEND_WORKSPACE_KEY,
        tenantId: this.userInfo.customerName,
        themeType: this.currentTheme === 'dark' ? ThemeType.DARK : ThemeType.LIGHT,
        theme: this.currentTheme === 'dark' ? this.suprsendservice.darkThemeConfig : this.suprsendservice.lightThemeConfig,
        primaryActionClickHandler: (notificationData: any) => {
          // Extract the URL from the notification data
          const url = notificationData.message.actions[0].url;
          const decodedUrl = new DOMParser().parseFromString(url, 'text/html').documentElement.textContent;
          const finalUrl = decodeURIComponent(decodedUrl);
          const isSingleEvent = notificationData.message.extra_data;
          this.handleRedirection(finalUrl, isSingleEvent);
        },
        pagination: true,
        pageSize: 20,
      },
    };

    // Initialize SuprSend Inbox
    initSuprSend(suprSendConfig);

    //add the dark theme config if the current theme is dark
    if (this.currentTheme === 'dark') {
      this.suprsendservice.injectDarkThemeStyles();
    } else {
      this.suprsendservice.removeDarkThemeStyles();
    }

    // Start observing for the SuprSend inbox modal
    this.setupInboxClickWatcher();
  }

  private setupInboxClickWatcher(): void {
    const bellIcon = document.querySelector('.css-1ygf8mx');
    if (!bellIcon) return;

    bellIcon.addEventListener('click', () => {
      this.suprsendservice.waitForModalThenAddButton();
    });
  }

  onNotificationSettingClick(event: Event) {
    event.stopPropagation();
    this.router.navigate(['/notification-setting'], {});
  }

  private handleRedirection(url: string, singleEvent: string) {
    if (!url) {
      return;
    }

    const [path, queryString] = url.split('?');
    const queryParams = new URLSearchParams(queryString);
    const queryParamsObj = {};
    queryParams.forEach((value, key) => {
      queryParamsObj[key] = value;
    });
    const isSingleEvent = singleEvent === 'single-event' || false;

    isSingleEvent ? this.handleSingleEventRedirection(path, queryParamsObj) : this.handleBulkEventRedirection(path, queryParamsObj);
  }

  private handleSingleEventRedirection(path, queryParams: IncidentDetailsParams | VideoRequestsPageQueryParams) {
    switch (path) {
      case AllowedPaths.safetyEvents: {
        const { tripId, driverId, eventIndex } = queryParams as IncidentDetailsParams;
        const incidentDetailsParams: IncidentDetailsParams = { tripId, driverId, eventIndex };
        this.incidentsService.getIncidentDetailsAndOpenDialog(incidentDetailsParams).pipe(takeUntil(this.ngUnsubscribe)).subscribe();
        break;
      }

      case AllowedPaths.videoRequests: {
        const { startTimeUTC, endTimeUTC, eventType, tripId, eventIndex, driverId, uploadRequestId } =
          queryParams as VideoRequestsPageQueryParams;
        const videoReqParams: VideoRequestsPageQueryParams = {
          startTimeUTC,
          endTimeUTC,
          eventType,
          tripId,
          eventIndex,
          driverId,
          uploadRequestId,
        };
        this.dvrService.getEventDetailsAndOpenDialog(videoReqParams).pipe(takeUntil(this.ngUnsubscribe)).subscribe();
        break;
      }

      default:
        this.snackbarService.failure(this.translate.instant('notificationConfiguration.Unable to find the event details at this moment'));
        break;
    }
  }

  private handleBulkEventRedirection(path, queryParams: SafetyEventsPageQueryParams | VideoRequestsPageQueryParams) {
    switch (path) {
      case AllowedPaths.safetyEvents: {
        const { startTimeUTC, endTimeUTC, eventType, tripId, eventIndex, driverId }: SafetyEventsPageQueryParams =
          queryParams as SafetyEventsPageQueryParams;
        const safetyEventsParams: SafetyEventsPageQueryParams = { startTimeUTC, endTimeUTC, eventType, tripId, eventIndex, driverId };
        this.router.navigate([path], { queryParams: safetyEventsParams });
        break;
      }

      case AllowedPaths.videoRequests: {
        const { startTimeUTC, endTimeUTC, eventType, tripId, eventIndex, driverId, uploadRequestId }: VideoRequestsPageQueryParams =
          queryParams as VideoRequestsPageQueryParams;
        const videoReqParams: VideoRequestsPageQueryParams = {
          startTimeUTC,
          endTimeUTC,
          eventType,
          tripId,
          driverId,
          uploadRequestId,
          eventIndex,
        };
        this.router.navigate([path], { queryParams: videoReqParams });
        break;
      }

      case AllowedPaths.assets: {
        const { tab, subTab, eventType, deviceId }: DiagnosticsQueryParams = queryParams as DiagnosticsQueryParams;
        const diagnosticsParams: DiagnosticsQueryParams = {
          tab,
          subTab,
          eventType,
          deviceId,
        };
        this.router.navigate([path], { queryParams: diagnosticsParams });
        break;
      }

      default:
        this.snackbarService.failure(this.translate.instant('notificationConfiguration.Unable to find the event details at this moment'));
        break;
    }
  }
}
